import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Button, TextField, Switch, FormControlLabel, Paper, List, ListItem, ListItemText, IconButton, Box, Snackbar, Alert,
  Stack, Chip, Tooltip, Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { ethers } from 'ethers';
import './App.css';

const darkTheme = createTheme({ palette: { mode: 'dark' } });
const lightTheme = createTheme({ palette: { mode: 'light' } });

export default function AdminDashboard({ isAdmin, dexContract, account, provider }) {
  const [themeMode, setThemeMode] = useState('dark');
  const [tokenAddress, setTokenAddress] = useState('');
  const [allowedTokens, setAllowedTokens] = useState([]);
  const [feeBps, setFeeBps] = useState('');
  const [platformEnabled, setPlatformEnabled] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Use direct RPC provider for all contract reads
  const rpcProvider = new ethers.JsonRpcProvider("https://serenity.ing/rpc");
  const readOnlyContract = React.useMemo(() => {
    if (!dexContract) return null;
    return new ethers.Contract(dexContract.target, dexContract.interface, rpcProvider);
  }, [dexContract]);

  // Fetch allowed tokens, fee, and trading status from contract
  useEffect(() => {
    if (!isAdmin || !readOnlyContract) return;
    async function fetchData() {
      await refreshAllowedTokens();
      try {
        const fee = await readOnlyContract.feeBps();
        setFeeBps(fee.toString());
        const enabled = await readOnlyContract.tradingEnabled();
        setPlatformEnabled(enabled);
      } catch (e) {
        setSnackbar({ open: true, message: 'Failed to fetch contract state', severity: 'error' });
      }
    }
    fetchData();
  }, [isAdmin, readOnlyContract]);

  // Helper to fetch token symbol
  const [tokenSymbols, setTokenSymbols] = useState({});
  useEffect(() => {
    async function fetchSymbols() {
      const symbols = {};
      for (const addr of allowedTokens) {
        try {
          const token = new ethers.Contract(addr, ["function symbol() view returns (string)"], rpcProvider);
          symbols[addr] = await token.symbol();
        } catch {
          symbols[addr] = '';
        }
      }
      setTokenSymbols(symbols);
    }
    if (allowedTokens.length > 0) fetchSymbols();
  }, [allowedTokens, rpcProvider]);

  // Helper to re-fetch allowed tokens from contract
  async function refreshAllowedTokens() {
    if (!readOnlyContract) return;
    try {
      const tokenCount = await readOnlyContract.getTokenListLength();
      let tokens = [];
      for (let i = 0; i < tokenCount; i++) {
        const t = await readOnlyContract.tokenList(i);
        tokens.push(t);
      }
      setAllowedTokens(tokens);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to refresh token list', severity: 'error' });
    }
  }

  const handleAddToken = async () => {
    try {
      const tx = await dexContract.addToken(tokenAddress);
      await tx.wait();
      setSnackbar({ open: true, message: 'Token added!', severity: 'success' });
      setTokenAddress('');
      await refreshAllowedTokens();
    } catch (e) {
      if (e.code === 4001) {
        setSnackbar({ open: true, message: 'You cancelled the MetaMask confirmation.', severity: 'info' });
      } else {
        setSnackbar({ open: true, message: e.message || 'Add token failed', severity: 'error' });
      }
    }
  };

  const handleRemoveToken = async (token) => {
    try {
      const tx = await dexContract.removeToken(token);
      await tx.wait();
      setSnackbar({ open: true, message: 'Token removed!', severity: 'success' });
      await refreshAllowedTokens();
    } catch (e) {
      if (e.code === 4001) {
        setSnackbar({ open: true, message: 'You cancelled the MetaMask confirmation.', severity: 'info' });
      } else {
        setSnackbar({ open: true, message: e.message || 'Remove token failed', severity: 'error' });
      }
    }
  };

  const handleSetFee = async () => {
    try {
      const tx = await dexContract.setFeeBps(feeBps);
      await tx.wait();
      setSnackbar({ open: true, message: 'Fee updated!', severity: 'success' });
    } catch (e) {
      if (e.code === 4001) {
        setSnackbar({ open: true, message: 'You cancelled the MetaMask confirmation.', severity: 'info' });
      } else {
        setSnackbar({ open: true, message: e.message || 'Set fee failed', severity: 'error' });
      }
    }
  };

  const handlePlatformToggle = async () => {
    try {
      const tx = await dexContract.setTradingEnabled(!platformEnabled);
      await tx.wait();
      setPlatformEnabled(!platformEnabled);
      setSnackbar({ open: true, message: `Trading ${!platformEnabled ? 'enabled' : 'disabled'}!`, severity: 'success' });
    } catch (e) {
      if (e.code === 4001) {
        setSnackbar({ open: true, message: 'You cancelled the MetaMask confirmation.', severity: 'info' });
      } else {
        setSnackbar({ open: true, message: e.message || 'Toggle failed', severity: 'error' });
      }
    }
  };

  if (!isAdmin) return null;

  return (
    <ThemeProvider theme={themeMode === 'dark' ? darkTheme : lightTheme}>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h3" fontWeight={900} color="primary" letterSpacing={2}>
            Admin Dashboard
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={themeMode === 'dark'}
                onChange={() => setThemeMode(themeMode === 'dark' ? 'light' : 'dark')}
                color="primary"
              />
            }
            label={themeMode === 'dark' ? 'Dark Mode' : 'Light Mode'}
          />
        </Box>
        <Paper elevation={6} sx={{ p: 4, mt: 2, borderRadius: 4, background: themeMode === 'dark' ? 'linear-gradient(135deg, #232526 0%, #414345 100%)' : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
          <Typography variant="h5" fontWeight={700} color="secondary" gutterBottom>
            Allowed Tokens
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
            <TextField
              label="Token Address"
              value={tokenAddress}
              onChange={e => setTokenAddress(e.target.value)}
              size="small"
              fullWidth
              variant="outlined"
              color="primary"
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddToken} size="large" color="primary" sx={{ fontWeight: 700, boxShadow: 2 }}>
              Add Token
            </Button>
          </Stack>
          <Divider sx={{ mb: 2 }} />
          <List>
            {allowedTokens.map(token => (
              <ListItem
                key={token}
                sx={{
                  bgcolor: themeMode === 'dark' ? '#232526' : '#e3eafc',
                  borderRadius: 2,
                  mb: 1,
                  boxShadow: 1,
                  display: 'flex',
                  alignItems: 'center',
                }}
                secondaryAction={
                  <Tooltip title="Remove Token" arrow>
                    <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveToken(token)} size="large" sx={{ color: 'error.main', bgcolor: 'error.light', '&:hover': { bgcolor: 'error.main', color: '#fff' } }}>
                      <DeleteIcon fontSize="medium" />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemText
                  primary={<Stack direction="row" alignItems="center" spacing={2}>
                    <Chip label={tokenSymbols[token] ? tokenSymbols[token] : token.slice(0, 6)} color="primary" sx={{ fontWeight: 700, fontSize: '1.1em', letterSpacing: 1 }} />
                    <Chip label={token} color="success" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.97em', ml: 1, fontWeight: 600, letterSpacing: 0.5 }} />
                  </Stack>}
                  secondary={null}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
        <Paper elevation={6} sx={{ p: 4, mt: 4, borderRadius: 4, background: themeMode === 'dark' ? 'linear-gradient(135deg, #232526 0%, #414345 100%)' : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
          <Typography variant="h5" fontWeight={700} color="secondary" gutterBottom>
            Trading Fee
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
            <TextField
              label="Fee (bps)"
              value={feeBps}
              onChange={e => setFeeBps(e.target.value)}
              size="small"
              type="number"
              variant="outlined"
              color="primary"
              sx={{ maxWidth: 200 }}
            />
            <Button variant="contained" onClick={handleSetFee} size="large" color="secondary" sx={{ fontWeight: 700, boxShadow: 2 }}>
              Set Fee
            </Button>
          </Stack>
        </Paper>
        <Paper elevation={6} sx={{ p: 4, mt: 4, borderRadius: 4, background: themeMode === 'dark' ? 'linear-gradient(135deg, #232526 0%, #414345 100%)' : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
          <Typography variant="h5" fontWeight={700} color="secondary" gutterBottom>
            Platform Status
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={platformEnabled}
                onChange={handlePlatformToggle}
                color="success"
              />
            }
            label={<Typography fontWeight={700} color={platformEnabled ? 'success.main' : 'error.main'}>{platformEnabled ? 'Trading Enabled' : 'Trading Disabled'}</Typography>}
          />
        </Paper>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </ThemeProvider>
  );
}
